/**
 * Every key read in a frame, and who gets to read it.
 *
 * An action press is an edge, and an edge belongs to whoever acts on it. The
 * two bug classes this game has had most of — one press reaching two
 * handlers, a key read by two consumers in one frame — are both a press being
 * observed rather than taken. So every read of a key goes through here, the
 * ways of reading one are named, and the one that takes the press is the only
 * one that can.
 *
 * Also where a headless run gets in. A script's presses and held keys are
 * merged here, underneath everything, so a scripted press is indistinguishable
 * from a real one all the way down.
 *
 * Two ways of reading a key, and only two:
 *
 *   pressed   the edge, taken. Nobody else this frame sees it
 *   held      down now — by a real key, a queued press, or a script's hold
 *
 * There used to be four. The sheet and throw keys ignored a script's hold and
 * the action key did not; jump read its edge without taking it. None of that
 * was a decision, it was where each reading happened to be written, and every
 * difference between them was a place a script and a player could disagree
 * about what a key was doing. Jump is an edge like any other now, and there is
 * one meaning of "held".
 */
/**
 * How a pad drives a cart: the steering curve (power on the stick), how much of
 * the gap to the stick the wheel closes a frame going out and coming back, and
 * the pedals' curve. Ours, by feel; only the input is shaped, never the cart.
 */
export const DRIVE = Object.freeze({ steerCurve: 1.6, ease: 0.2, settle: 0.32, pedalCurve: 1.3 });

export function createFrameInput({ input }) {
  /** Where the pad's wheel has got to. */
  let padSteer = 0;
  /*
   * `scripted` is how a headless run drives the game. It is merged here rather
   * than applied by calling `player.update()` a second time, because two
   * updates in one frame is two frames of friction against one of
   * acceleration — a body driven that way crawls, and the test that "proves"
   * the sprint works reports a walking pace instead.
   */
  const scripted = {
    move: null,
    sprint: false,
    jump: false,
    /**
     * Actions a headless run is holding down rather than tapping.
     *
     * A queued press is one frame of held and one frame of pressed, and the
     * fan needs a fifth of a second of held with no press in it. Re-queuing
     * the press every frame gets you fourteen taps rather than one hold, and
     * the tap branch runs first, so a script trying to open the menu performed
     * the top option fourteen times instead. This is the difference between
     * the two, said out loud: what is in here is down and is not being pressed.
     */
    hold: new Set(),
  };

  /**
   * One-frame action presses queued by a headless run.
   *
   * A test that reaches past the input layer to call `setup.placeTable`
   * directly proves that `setup.placeTable` works and nothing about whether
   * the game does.
   */
  const queued = new Set();

  /**
   * Presses already spent this frame.
   *
   * It did not used to exist: the prompt chain runs handler after handler,
   * each one asking "was E pressed?", and a handler that answered yes and did
   * its work returned null — the same null it returns for "nothing here". So
   * the next handler in the chain asked the same question, got the same yes,
   * and did its work too.
   *
   * One press at a parked cart therefore took the handle *and* swept whatever
   * happened to be lying nearest into the cart. Which prop that was depended on
   * where the player's last step landed, so the shift either went normally or
   * began with a roll of gaffer tape in the cart and the player's hands full,
   * a fifth of the time, with nothing in the simulation being random.
   *
   * Reading a press no longer means observing it. It means taking it.
   */
  const spent = new Set();

  return {
    scripted,

    /** A headless run's one-frame press. */
    queue(action) {
      queued.add(action);
    },

    pressed(action) {
      if (spent.has(action)) return false;
      // Something being held is not something being pressed, which is the
      // whole distinction the fan is built on.
      if (scripted.hold.has(action)) return false;
      if (!(input.pressed(action) || queued.has(action))) return false;
      spent.add(action);
      return true;
    },

    held(action) {
      return input.held(action) || queued.has(action) || scripted.hold.has(action);
    },

    /** The jump edge, taken like any other — or a script asking for one. */
    jumped() {
      return scripted.jump || this.pressed('jump');
    },

    /** The stick, or a script's. */
    get move() {
      return scripted.move ?? input.move;
    },

    /**
     * What a cart is told: how hard forward, how far over.
     *
     * The stick's own forward, unless the pad's triggers are in use, in which
     * case the right trigger is the gas and the left is the brake and reverse,
     * and the stick only steers, the way every driving game on a pad works. A
     * script's move wins over both, as it does for walking.
     */
    get drive() {
      if (scripted.move) return { forward: scripted.move.y, turn: scripted.move.x };
      const move = input.move;
      const pad = input.padState;
      const pedals = pad && (pad.gas > 0 || pad.brake > 0);
      if (!input.padLive || !pad || (input.device !== 'gamepad' && !pedals)) {
        padSteer = 0;
        return { forward: move.y, turn: move.x };
      }
      /*
       * A pad is shaped, the keyboard never is. The stick steers on a curve, so
       * the first third of the throw is fine correction and the last third is
       * full lock, and the wheel eases toward where the stick is (faster back
       * to centre than away from it) instead of snapping. The triggers get a
       * gentle curve too, for backing up to a hitch. Read once a frame.
       */
      const target = Math.sign(move.x) * Math.abs(move.x) ** DRIVE.steerCurve;
      const rate = Math.abs(target) < Math.abs(padSteer) ? DRIVE.settle : DRIVE.ease;
      padSteer += (target - padSteer) * rate;
      if (Math.abs(padSteer) < 1e-3) padSteer = 0;
      if (pedals) {
        return { forward: pad.gas ** DRIVE.pedalCurve - pad.brake ** DRIVE.pedalCurve, turn: padSteer };
      }
      return { forward: move.y, turn: padSteer };
    },

    get sprinting() {
      return scripted.sprint || input.held('sprint');
    },

    get look() {
      return input.look;
    },

    /** The start of a frame: nothing has been taken yet. */
    beginFrame() {
      spent.clear();
    },

    /** The end of one, or a frame a panel swallowed: a script's presses are gone. */
    endFrame() {
      queued.clear();
    },
  };
}
